import React, { useEffect, useContext } from 'react';
import { AppContext } from '../provider/AppContextProvider';
import { CmpIdentifier } from '../helper/enums';
import { Platform } from '../helper/interfaces';
import { TrackingInformationInterface } from '../helper/PageInformationInterface';
import useScript from '../helper/useScript';
import { ConsentContext } from '../provider/ConsentContextProvider';
import Head from 'next/head';
import { executePianoExperienceOnce } from '../helper/piano';

declare global {
  interface Window {
    pa: any;
    _pac: any;
  }
}

interface Data {
  trackingInformation: TrackingInformationInterface
}

function filterOutCommas(string: string) {
  return string.replace(/,/g, "");
}

export function sendTrackingEvent(eventName: string, trackingInformation: TrackingInformationInterface, isTrackingAllowed: boolean, subscription?: any, user?: any, appInfo?: any, tp?: any) {
  try {
        window.pa.setConfigurations({
          site: trackingInformation?.site,
          collectDomain: process.env.NEXT_PUBLIC_PIANO_COLLECT_DOMAIN
        });
      }
      catch(e) {
        console.error(e);
      }

      try {
        if (isTrackingAllowed) {
          window.pa.consent.setMode('opt-in');
        } else {
          window.pa.consent.setMode('opt-out');
        }
      }
      catch(e) {
        console.error(e);
      }

      const pianoTags: string[] = [];
      
      const trackingData = {} as any;
      trackingData.priority = trackingInformation?.priority;

      if(trackingInformation?.article) {
        trackingData.artikel = trackingInformation?.article
        pianoTags.push(`artikel:${trackingInformation?.article}`);
      }
      
      if(trackingInformation?.authors) {
        trackingData.author = trackingInformation?.authors.join(";");
        trackingInformation?.authors.map((author) => `author:${filterOutCommas(author)}`).forEach((tag) => pianoTags.push(tag));
      }
      
      if(trackingInformation?.branding) {
        trackingData.branding = trackingInformation?.branding
      }

      if(trackingInformation?.categories1) {
        trackingData.category_1 = trackingInformation?.categories1.join(";");
        trackingInformation?.categories1.map((category) => `category_1:${filterOutCommas(category)}`).forEach((tag) => pianoTags.push(tag));
      }
      if(trackingInformation?.categories2) {
        trackingData.category_2 = trackingInformation?.categories2.join(";");
        trackingInformation?.categories2.map((category) => `category_2:${filterOutCommas(category)}`).forEach((tag) => pianoTags.push(tag));
      }
      
      if(trackingInformation?.cities) {
        trackingData.ort = trackingInformation?.cities.join(";");
        trackingInformation?.cities.map((city) => `ort:${filterOutCommas(city)}`).forEach((tag) => pianoTags.push(tag));
      }
      
      if(trackingInformation?.containsContentTypes) {
        trackingData.contains_content_type = trackingInformation?.containsContentTypes;//.join(";");
        trackingInformation?.containsContentTypes.map((contentType) => `contains_content_type:${filterOutCommas(contentType)}`).forEach((tag) => pianoTags.push(tag));
      }

      if(trackingInformation?.contentId) {
        trackingData.content_id = trackingInformation?.contentId
        pianoTags.push(`content_id:${trackingInformation?.contentId}`);
      }
      if(trackingInformation?.contentType) {
        trackingData.content_type = trackingInformation?.contentType
        pianoTags.push(`content_type:${trackingInformation?.contentType}`);
      }
      
      if(trackingInformation?.dpaCode) {
        trackingData.dpa_code = trackingInformation?.dpaCode
      }

      if(trackingInformation?.contentId) {
        trackingData.content_id = trackingInformation?.contentId
      }
      if(trackingInformation?.contentType) {
        trackingData.content_type = trackingInformation?.contentType
      }

      if(trackingInformation?.paidContent) {
        trackingData.paid_content = trackingInformation?.paidContent
        pianoTags.push(`paid_content:${trackingInformation?.paidContent}`);
      }

      if(trackingInformation?.priority) {
        trackingData.priority = trackingInformation?.priority
        pianoTags.push(`priority:${trackingInformation?.priority}`);
      }

      if(trackingInformation?.regions) {
        trackingData.region = trackingInformation?.regions.join(";")
        trackingInformation?.regions.map((region) => `region:${filterOutCommas(region)}`).forEach((tag) => pianoTags.push(tag));
      }

      if(trackingInformation?.subSections) {
        trackingData.sub_sections = trackingInformation?.subSections;
        pianoTags.push(`sub_sections:${filterOutCommas(trackingInformation?.subSections)}`);
      }

      if(trackingInformation?.page) {
        trackingData.page = trackingInformation?.page
        pianoTags.push(`page:${trackingInformation?.page}`);
      }

      if(trackingInformation?.siteLevel2) {
        trackingData.site_level2 = trackingInformation?.siteLevel2
        pianoTags.push(`site_level2:${trackingInformation?.siteLevel2}`);
      }

      if(trackingInformation?.articleTypes) {
        trackingData.artikeltyp = trackingInformation?.articleTypes.join(";");
        trackingInformation?.articleTypes.map((type) => `artikeltyp:${filterOutCommas(type)}`).forEach((tag) => pianoTags.push(tag));
      }

      if(trackingInformation?.editorialTeams) {
        trackingData.editorial_team = trackingInformation?.editorialTeams.join(";")
        trackingInformation?.editorialTeams.map((team) => `editorial_team:${filterOutCommas(team)}`).forEach((tag) => pianoTags.push(tag));
      }

      if(trackingInformation?.firstPublication) {
        trackingData["d:first_publication"] = trackingInformation?.firstPublication
        pianoTags.push(`first_publication:${filterOutCommas(trackingInformation?.firstPublication)}`);
      }

      if(trackingInformation?.lastPublication) {
        trackingData["d:last_publication"] = trackingInformation?.lastPublication
        pianoTags.push(`last_publication:${filterOutCommas(trackingInformation?.lastPublication)}`);
      }

      if(trackingInformation?.sections) {
        trackingData.sections = trackingInformation?.sections
        pianoTags.push(`sections:${trackingInformation?.sections}`);
      }

      const platform = "app";
      trackingData.platform = platform;
      pianoTags.push(`platform:${platform}`);
      
      if(subscription && (subscription.nnPlusApp || subscription.nnPlusWeb)) {
        trackingData.user_status = "paid"
      }
      else if(user) {
        trackingData.user_status = "lgdin"
      }
      else {
        trackingData.user_status = "anon"
      }

      trackingData.url = `${document.location.href.split("//")[1]}`
      if(document.referrer) {
        trackingData.referrer = `${document.referrer.split("//")[1]}`
      }

      trackingData.push_notification_settings = (appInfo.pushesEnabled || appInfo.pushSubscriptionId) ? "Allowed" : "Disallowed";

      if(user && user.tokenId) {
        trackingData.user_token = user.tokenId;
      }

      if(trackingInformation?.authors) {
        trackingData.content_author = trackingInformation?.authors.join(";")
        tp?.push(["setContentAuthor", trackingInformation?.authors.join(";")]);
      }

      if(trackingInformation?.siteLevel2) {
        trackingData.content_section = trackingInformation?.siteLevel2
      }

      if(trackingInformation?.firstPublication) {
        trackingData.content_created = trackingInformation?.firstPublication
        tp?.push(["setContentCreated", trackingInformation?.firstPublication]);
      }

      if(trackingInformation?.goalType) {
        trackingData.goal_type = trackingInformation?.goalType
      }

      tp?.push(["setTags", pianoTags.flat()]);

      if(appInfo.platform) {
        if(appInfo.platform === Platform.iOS) {
          trackingData.system = "ios"
        }
        else if(appInfo.platform === Platform.Android) {
          trackingData.system = "android"
        }
        tp?.push(["setContentIsNative", "true"]);
      }
      else {
        tp?.push(["setContentIsNative", "false"]);
      }

      try {
        //console.log("** tracking: sendEvent ", eventName, trackingData, "consent given", isTrackingAllowed);
        window.pa.sendEvent(eventName, trackingData);
      }
      catch(e) {
        console.error("** tracking: Error sending tracking data window.pa.sendEvent", e);
      }
  
}

export default function Tracking(props: Data) {
  const status = useScript('https://tag.aticdn.net/piano-analytics.js')
  const { isAllowedByCmp, subscription, user, headerDisabled, appInfo } = React.useContext(AppContext);
  const { getProviderId } = useContext(ConsentContext);
  const cmpVendorId = getProviderId(CmpIdentifier.AtInternet);
  const isTrackingAllowed = cmpVendorId && isAllowedByCmp(cmpVendorId)
  
  const trackingInformation = props.trackingInformation;

  useEffect(() => {
    if(cmpVendorId == "" || (isAllowedByCmp(cmpVendorId) != false && isAllowedByCmp(cmpVendorId) != true) || status !== 'ready')
    {
      return;
    }
    let tp = window["tp"] || [];

    if(status === 'ready') {
      sendTrackingEvent('page.display', trackingInformation, (isTrackingAllowed == true), subscription, user, appInfo, tp);
      executePianoExperienceOnce(tp);
    }

  },[isTrackingAllowed, cmpVendorId, status, subscription, user, appInfo])// eslint-disable-line react-hooks/exhaustive-deps


  return (<>
  <Head>
    {props.trackingInformation?.contentId && <meta name="cXenseParse:articleid" content={props.trackingInformation?.contentId}/>}
    <meta name="cXenseParse:oly-affiliate-article" content={props.trackingInformation?.affiliate ? "true" : "false"}/>
    {props.trackingInformation?.articleTypes && <meta name="cXenseParse:oly-article-category" content={props.trackingInformation?.articleTypes.join(";")} data-separator=";"/>}
    {props.trackingInformation?.paidContent && <meta name="cXenseParse:oly-content-status" content={props.trackingInformation?.paidContent}/>}
    {props.trackingInformation?.contentType && <meta name="cXenseParse:oly-content-type" content={props.trackingInformation?.contentType}/>}
    {props.trackingInformation?.cities && <meta name="cXenseParse:oly-location" content={props.trackingInformation?.cities.join(";")} data-separator=";"/>}
    {props.trackingInformation?.priority && <meta name="cXenseParse:oly-priority" content={props.trackingInformation?.priority}/>}
    {props.trackingInformation?.regions && <meta name="cXenseParse:oly-region" content={props.trackingInformation?.regions.join(";")} data-separator=";"/>}
    {props.trackingInformation?.topic && props.trackingInformation?.topic?.length > 0 && <meta name="cXenseParse:oly-topic" content={props.trackingInformation?.topic} data-separator=";"/>}
    {props.trackingInformation?.topline && <meta name="cXenseParse:oly-topline" content={props.trackingInformation?.topline}/>}
    {props.trackingInformation?.advertorial === true && <meta name="cXenseParse:oly-advertorial" content="true"/>}
    {props.trackingInformation?.advertorial === false && <meta name="cXenseParse:oly-advertorial" content="false"/>}
  </Head>
  </>
  )
}